import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, RefreshCw, Wand2, Loader2, Play, Pause } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { mixToWav } from "@/lib/wav";

// Records vocals while the chosen melody plays, then blends them into one track.
export const VocalRecorder = ({ melodyUrl, onMixed }) => {
  const { t } = useLang();
  const [phase, setPhase] = useState("idle"); // idle | recording | recorded | mixing | ready
  const [balance, setBalance] = useState(0.6);
  const [mixUrl, setMixUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const melodyRef = useRef(null);
  const previewRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const vocalRef = useRef(null);

  useEffect(() => {
    melodyRef.current = new Audio(melodyUrl);
    melodyRef.current.crossOrigin = "anonymous";
    return () => { try { melodyRef.current?.pause(); } catch (e) {} };
  }, [melodyUrl]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        vocalRef.current = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setPhase("recorded");
        doMix(vocalRef.current, balance);
      };
      recRef.current = rec;
      const mel = melodyRef.current;
      mel.currentTime = 0;
      await mel.play().catch(() => {});
      rec.start();
      setPhase("recording");
      mel.onended = () => { if (recRef.current && recRef.current.state === "recording") stop(); };
    } catch (e) {
      toast.error(t("mic_denied"));
    }
  };

  const stop = () => {
    try { melodyRef.current?.pause(); } catch (e) {}
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const doMix = async (vocalBlob, bal) => {
    setPhase("mixing");
    try {
      const blob = await mixToWav(melodyUrl, vocalBlob, bal);
      const url = URL.createObjectURL(blob);
      setMixUrl(url);
      onMixed(blob);
      setPhase("ready");
    } catch (e) {
      toast.error("Could not mix the audio on this device. You can upload a finished take instead.");
      setPhase("recorded");
    }
  };

  const reRecord = () => {
    setPhase("idle"); setMixUrl(null); vocalRef.current = null; onMixed(null);
  };

  const togglePreview = () => {
    const a = previewRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="vocal-recorder">
      {phase === "idle" && (
        <button onClick={start} data-testid="recorder-start" className="w-full h-14 rounded-xl font-ui font-bold text-lg flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
          <Mic size={22} /> {t("ws_record")}
        </button>
      )}
      {phase === "recording" && (
        <button onClick={stop} data-testid="recorder-stop" className="w-full h-14 rounded-xl font-ui font-bold text-lg flex items-center justify-center gap-2 sk-glow" style={{ background: "#dc2626", color: "#fff" }}>
          <Square size={20} fill="currentColor" /> {t("ws_stop")} · {t("recording_now")}
        </button>
      )}
      {phase === "mixing" && (
        <div className="h-14 flex items-center justify-center gap-2 font-ui font-bold" style={{ color: "var(--text-2)" }}><Loader2 className="animate-spin" size={20} /> {t("ws_mixing")}</div>
      )}
      {(phase === "recorded" || phase === "ready") && (
        <div className="space-y-3">
          <div>
            <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_balance")}</span>
            <input type="range" min={0} max={1} step={0.05} value={balance} data-testid="recorder-balance"
              onChange={(e) => setBalance(parseFloat(e.target.value))}
              onMouseUp={() => vocalRef.current && doMix(vocalRef.current, balance)}
              onTouchEnd={() => vocalRef.current && doMix(vocalRef.current, balance)}
              className="w-full mt-1" style={{ accentColor: "var(--accent)" }} />
          </div>
          {mixUrl && (
            <div className="flex items-center gap-3">
              <button onClick={togglePreview} data-testid="recorder-preview" className="grid place-items-center w-12 h-12 rounded-full shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
                {playing ? <Pause size={20} /> : <Play size={20} fill="currentColor" />}
              </button>
              <span className="font-ui text-sm font-semibold" style={{ color: "var(--text-2)" }}><Wand2 size={14} className="inline" /> {t("ws_mix_preview") || "Mix ready"}</span>
              <audio ref={previewRef} src={mixUrl} onEnded={() => setPlaying(false)} className="hidden" />
            </div>
          )}
          <button onClick={reRecord} data-testid="recorder-rerecord" className="w-full h-11 rounded-xl font-ui font-bold text-sm flex items-center justify-center gap-2" style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}>
            <RefreshCw size={16} /> {t("ws_rerecord")}
          </button>
        </div>
      )}
    </div>
  );
};
